import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { LogOut, User, Briefcase, Shield, Users, Target } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { clearSensitiveData } from "@/lib/security/secureStorage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const Navbar = () => {
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      // Clear all sensitive data from storage before signing out
      clearSensitiveData();

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      toast({
        title: "Signed Out",
        description: "You have been successfully signed out. All sensitive data cleared.",
      });

      navigate("/");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to sign out.",
        variant: "destructive",
      });
    }
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          Second Chance Connect
        </Link>

        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/ai-resume-builder">
              Build Resume
            </Link>
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <User className="h-4 w-4" />
                  {user.email}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/ai-resume-builder")} className="cursor-pointer">
                  Build AI Resume
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/connect")} className="cursor-pointer">
                  Connect
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/connections")} className="cursor-pointer">
                  My Connections
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/messages")} className="cursor-pointer">
                  Messages
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/edit-profile")} className="cursor-pointer">
                  <User className="mr-2 h-4 w-4" />
                  Edit Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="default">Sign In</Button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Choose your account type</h4>
                    <p className="text-sm text-muted-foreground">Select how you'd like to sign in</p>
                  </div>
                  <div className="grid gap-2">
                    <Button asChild variant="outline" className="justify-start gap-2">
                      <Link to="/jobseeker-login">
                        <Target className="h-4 w-4" />
                        Job Seekers
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="justify-start gap-2">
                      <Link to="/employer-login">
                        <Briefcase className="h-4 w-4" />
                        Employers
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="justify-start gap-2">
                      <Link to="/community-org-auth">
                        <Users className="h-4 w-4" />
                        Community Organisations
                      </Link>
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </nav>
  );
};
